import { createSelector } from '@ngrx/store';
import { CAAction, MemberBadgesActions, Organisations } from '../actions';
import { DataListState, INITIAL_DATA_LIST_STATE } from '../data-list.state';
import { ReducerStateFactory } from '../state-factory.reducer';
import {
  OrganisationBrandingDto,
  OrganisationConfigDto,
  OrganisationDto,
  OrganisationFeaturesDto,
  OrganisationPreferenceDto,
  PermissionDto,
} from '@careapp-group/ng-sdk';
import { ORGANISATION_ROLE_HEIRARCHY } from '@careapp-group/core';

export type OrganisationState = DataListState<OrganisationDto> & {
  selectable: string[];
  roles: { [orgId: string]: string[] };
  permissions: { [orgId: string]: PermissionDto[] };
  loadingBranding: boolean;
  loadingConfiguration: boolean;
  loadingPreferences: boolean;
  loadingOwn: boolean;
  preferences: { [id: string]: OrganisationPreferenceDto };
  branding: { [id: string]: OrganisationBrandingDto };
  configuration: { [id: string]: OrganisationConfigDto };
  features: { [id: string]: OrganisationFeaturesDto };
};

class OrganisationsState extends ReducerStateFactory<OrganisationDto> {
  readonly initialState: OrganisationState = {
    ...INITIAL_DATA_LIST_STATE,
    loadingBranding: false,
    loadingConfiguration: false,
    loadingPreferences: false,
    roles: {},
    permissions: {},
    loadingOwn: false,
    selectable: [],
    preferences: {},
    branding: {},
    configuration: {},
    features: {},
  };

  constructor() {
    super(Organisations);
  }

  public reducer(state: OrganisationState = this.initialState, action: CAAction) {
    switch (action.type) {
      case Organisations.REQUEST_OWN:
        return { ...state, loadingOwn: true };
      case Organisations.REQUEST_PREFERENCES:
      case Organisations.UPDATE_PREFERENCES:
        return { ...state, loadingPreferences: true };
      case Organisations.RESPONSE_SUCCESS_OWN: {
        const orgs: OrganisationDto[] = action.payload;
        const newEntities = action.payload.reduce(
          (prev, next) => ({ ...prev, [next.id]: next }),
          {},
        );
        const newIds = Array.from(new Set<string>([...state.ids, ...Object.keys(newEntities)]));
        const roles = action.payload.reduce(
          (prev, next) => ({
            ...prev,
            [next.id]: next.roles,
          }),
          {},
        );
        const permissions = action.payload.reduce(
          (prev, next) => ({
            ...prev,
            [next.id]: next.permissions,
          }),
          {},
        );
        const branding: { [id: string]: OrganisationBrandingDto } = orgs.reduce<{
          [id: string]: OrganisationBrandingDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.branding }), {});
        const configuration: { [id: string]: OrganisationConfigDto } = orgs.reduce<{
          [id: string]: OrganisationConfigDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.config }), {});
        const preferences: { [id: string]: OrganisationPreferenceDto } = orgs.reduce<{
          [id: string]: OrganisationPreferenceDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.preferences }), {});
        const features: { [id: string]: OrganisationFeaturesDto } = orgs.reduce<{
          [id: string]: OrganisationFeaturesDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.features }), {});
        return {
          ...state,
          loadingOwn: false,
          ids: newIds,
          roles,
          permissions,
          branding,
          configuration,
          features,
          preferences,
          entities: { ...state.entities, ...newEntities },
          selectable: Object.keys(newEntities),
        };
      }
      case Organisations.RESPONSE_SUCCESS_ALL: {
        const orgs: OrganisationDto[] = action.payload;
        const branding: { [id: string]: OrganisationBrandingDto } = orgs.reduce<{
          [id: string]: OrganisationBrandingDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.branding }), {});
        const configuration: { [id: string]: OrganisationConfigDto } = orgs.reduce<{
          [id: string]: OrganisationConfigDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.config }), {});
        const preferences: { [id: string]: OrganisationPreferenceDto } = orgs.reduce<{
          [id: string]: OrganisationPreferenceDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.preferences }), {});
        const features: { [id: string]: OrganisationFeaturesDto } = orgs.reduce<{
          [id: string]: OrganisationFeaturesDto;
        }>((accum, org) => ({ ...accum, [org.id]: org.features }), {});
        return { ...super.reducer(state, action), configuration, preferences, branding, features };
      }
      case Organisations.FAIL_UPDATE_PREFERENCES: {
        return { ...state, loadingPreferences: false };
      }
      case Organisations.SUCCESS_UPDATE_PREFERENCES:
      case Organisations.RESPONSE_SUCCESS_PREFERENCES: {
        const preferences = { ...state.preferences };
        preferences[action.payload.organisationId] = action.payload;
        return { ...state, preferences, loadingPreferences: false };
      }
      case Organisations.REQUEST_BRANDING: {
        return { ...state, loadingBranding: true };
      }
      case Organisations.RESPONSE_SUCCESS_BRANDING:
      case Organisations.SUCCESS_UPDATE_BRANDING_COLORS:
      case Organisations.SUCCESS_UPDATE_BRANDING_IMAGE:
        const branding = { ...state.branding };
        branding[action.payload.organisationId] = action.payload;
        return { ...state, branding, loadingBranding: false };
      case Organisations.RESPONSE_FAIL_BRANDING:
        return { ...state, loadingBranding: false };

      case Organisations.REQUEST_ORG_CONFIG:
        return { ...state, loadingConfiguration: true };
      case Organisations.UPDATE_ORG_CONFIG:
        return { ...state, loadingConfiguration: true };
      case Organisations.RESPONSE_SUCCESS_ORG_CONFIG:
      case Organisations.SUCCESS_UPDATE_ORG_CONFIG:
        const configuration = { ...state.configuration };
        configuration[action.payload.organisationId] = action.payload;
        return { ...state, configuration, loadingConfiguration: false };
      case Organisations.RESPONSE_FAIL_ORG_CONFIG:
      case Organisations.FAIL_UPDATE_ORG_CONFIG:
        return { ...state, loadingConfiguration: false };

      // If the user has updated their own permission, update in the org state as well.
      case MemberBadgesActions.ResponseSuccessAddBadgeToPermission.type:
      case MemberBadgesActions.ResponseSuccessRemoveBadgeFromPermission.type:
        const newPermission: PermissionDto = action.payload;
        if (newPermission.modelName === 'Organisation') {
          const updatedOrg = { ...state.entities[newPermission.modelId] };
          // Check if this permission is for the logged in user.
          const loggedInMemberId = (updatedOrg?.permissions ?? []).length
            ? updatedOrg.permissions[0].memberId
            : null;
          if (!!loggedInMemberId && newPermission.memberId == loggedInMemberId) {
            const updatedOrgPermissions = [
              ...updatedOrg.permissions.filter((permission) => permission.id !== newPermission.id),
              newPermission,
            ];
            updatedOrg.permissions = updatedOrgPermissions;
            return {
              ...state,
              entities: { ...state.entities, [newPermission.modelId]: updatedOrg },
            };
          }
        }
    }
    return super.reducer(state, action);
  }
}

export const state = new OrganisationsState();

export const createSelectors = (rootReducer: any) => ({
  getPreferencesLoading: createSelector(
    rootReducer,
    (state: OrganisationState) => state.loadingPreferences,
  ),
  getPersisted: createSelector(
    rootReducer,
    (_state: OrganisationState = state.initialState) =>
      <OrganisationState>{
        // Ensure we don't get loading keys etc.
        ...state.initialState,
        // only need this so we can pre-select for users with multiple orgs CA-994
        selectedId: _state.selectedId,
      },
  ),
  getConfigurationLoading: createSelector(
    rootReducer,
    (state: OrganisationState) => state.loadingConfiguration,
  ),
  getSelectableIds: createSelector(rootReducer, (state: OrganisationState) => state.selectable),
  getSelectable: createSelector(rootReducer, (state: OrganisationState) =>
    state.selectable.map((id) => state.entities[id]).filter((org) => (<any>org)?.enabled),
  ),
  currentOrgPreferences: createSelector(
    rootReducer,
    (state: OrganisationState) => state.preferences[state.selectedId],
  ),
  currentOrgBranding: createSelector(
    rootReducer,
    (state: OrganisationState) => state.branding[state.selectedId],
  ),
  currentOrgConfiguration: createSelector(
    rootReducer,
    (state: OrganisationState) => state.configuration[state.selectedId],
  ),
  currentOrgFeatures: createSelector(
    rootReducer,
    (state: OrganisationState) => state.features[state.selectedId],
  ),
  currentOrgRoles: createSelector(rootReducer, (state: OrganisationState) => {
    return state.roles[state.selectedId] || [];
  }),
  /// Returns the role names the user has across all organisations
  /// Filters to only include roles that exist in ORGANISATION_ROLE_HEIRARCHY
  /// (excludes customer roles and tenant roles)
  allOrgRoles: createSelector(rootReducer, (state: OrganisationState) => {
    const allRoles = Object.values(state.roles).reduce(
      (combined, current) => [...combined, ...current],
      [],
    );
    return Array.from(
      new Set(allRoles.filter((role) => ORGANISATION_ROLE_HEIRARCHY.includes(role))),
    );
  }),
  currentOrgPermissions: createSelector(rootReducer, (state: OrganisationState) => {
    return state?.permissions[state.selectedId] || [];
  }),

  // Return a map of selectable Org Ids and the user's org roles within each.
  getSelectableRoles: createSelector(
    rootReducer,
    (state: OrganisationState): { [orgId: string]: string[] } => {
      return (state?.selectable ?? []).reduce((acc, selectableOrgId) => {
        return { ...acc, [selectableOrgId]: state.roles[selectableOrgId] ?? [] };
      }, {});
    },
  ),
});

export function reducer(...args) {
  return state.reducer.apply(state, args);
}
